import { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '@/components/ui/responsive-dialog';
import { usePlans, useBillingInfo } from '@/api/queries';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { Check, Sparkles, Zap, Crown, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPriceBRL } from '@my360/shared';

/**
 * In-flow plan picker — opens from the BillingQuickPanel "Mudar plano"
 * button. Lets the user pick a target plan WITHOUT being routed to the
 * full /pricing page. Tap → resolves a BillingSku and hands it back via
 * onPick → caller opens BillingCheckoutDialog.
 *
 * Mobile: vaul drawer (via ResponsiveDialog) — cards stack vertically.
 * Desktop: modal — cards stack vertically too (drawer/modal already
 * adapts to width). Compact + scannable; no slider needed since at
 * most ~3 candidate plans are shown after filtering.
 *
 * Filtering rules:
 *   - Hide the user's CURRENT plan (otherwise upgrade is a no-op).
 *   - Hide free plan (no checkout) and beta if user not in beta cohort.
 *   - Show: pro, business, enterprise — sorted by price asc.
 */

interface PlanPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (sku: { kind: 'plan'; slug: string; name: string; priceCents: number }) => void;
}

const PLAN_ICON: Record<string, React.ElementType> = {
  pro: Sparkles,
  business: Zap,
  enterprise: Crown,
  beta: Sparkles,
};

// Highlights are resolved via t() inside the component using index keys.

export function PlanPickerDialog({ open, onOpenChange, onPick }: PlanPickerDialogProps) {
  const { t } = useTranslation();
  const { data: plansData } = usePlans();
  const { data: billing } = useBillingInfo();
  // BillingInfo expõe tenant.plan (string slug) — não tem .plan.slug.
  const currentSlug = billing?.data?.tenant?.plan ?? 'free';
  // Mobile é SEMPRE drawer (bottom-sheet) — modal centralizado em tela
  // pequena lê como um popup de sistema, não como parte do produto. No
  // drawer os planos empilham na vertical; o carrossel com peek fica
  // só no desktop, onde há largura pra ele respirar.
  const isMobile = useIsMobile();
  const forceMode: 'drawer' | 'modal' = isMobile ? 'drawer' : 'modal';

  const candidates = useMemo(() => {
    const all = plansData?.data ?? [];
    // Show ALL real plans (incl. the one the user is on — flagged as current and
    // non-selectable). Hide internal 'test' and the promo 'beta' (unless it's
    // the user's current plan, so it still shows as their plan).
    return all
      .filter((p) => p.slug !== 'test' && (p.slug === currentSlug || p.slug !== 'beta'))
      .sort((a, b) => a.price - b.price);
  }, [plansData, currentSlug]);

  // ─── Carousel state ─────────────────────────────────────────────────
  // Cards rolam horizontalmente com snap. Track o ativo via scroll
  // listener pra dots e setas. Reseta pro 0 quando abre/fecha.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (open) setActiveIdx(0);
  }, [open]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        setActiveIdx(idx);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [candidates.length]);

  const scrollToIdx = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  // Um único card, usado nos dois layouts: pilha vertical (drawer mobile)
  // e carrossel com peek (modal desktop).
  const renderCard = (plan: (typeof candidates)[number]) => {
    const Icon = PLAN_ICON[plan.slug] || Sparkles;
    const allHighlights = t('billingUi:planPicker.highlights', { returnObjects: true });
    const highlightKeys: string[] = (plan.slug in allHighlights ? allHighlights[plan.slug as keyof typeof allHighlights] : []) as string[];
    const highlights = Array.isArray(highlightKeys) ? highlightKeys : [];
    const isCurrent = plan.slug === currentSlug;
    const isPopular = plan.slug === 'pro' && !isCurrent;
    return (
      <button
        key={plan.slug}
        type="button"
        disabled={isCurrent}
        onClick={() => {
          if (isCurrent) return;
          onPick({
            kind: 'plan',
            slug: plan.slug,
            name: plan.name,
            priceCents: plan.price,
          });
          onOpenChange(false);
        }}
        className={cn(
          'group text-left rounded-2xl border p-5 transition-all',
          // Carrossel: w-[78%] deixa ~22% do próximo card de "peek".
          // Pilha: largura cheia.
          isMobile ? 'w-full' : 'snap-start shrink-0 w-[78%]',
          'bg-[var(--color-surface-elevated)] border-[var(--color-border)]',
          isCurrent
            ? 'cursor-default opacity-80 ring-1 ring-[var(--color-accent)]/40 border-[var(--color-accent)]/40'
            : 'hover:border-[var(--color-primary)]/50 hover:bg-[var(--color-surface-hover)] active:scale-[0.99]',
          isPopular && 'border-[var(--color-primary)]/40 ring-1 ring-[var(--color-primary)]/20',
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex h-12 w-12 items-center justify-center rounded-xl shrink-0',
              isPopular
                ? 'bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-accent)] text-white shadow-md'
                : 'bg-[var(--color-primary)]/10 text-[var(--color-primary)] border border-[var(--color-primary)]/20',
            )}
          >
            <Icon className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-lg font-bold tracking-tight capitalize"
                style={{ color: 'var(--color-text)' }}
              >
                {plan.name}
              </span>
              {isCurrent ? (
                <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded bg-[var(--color-accent)]/15 text-[var(--color-accent)]">
                  {t('billingUi:planPicker.currentPlan')}
                </span>
              ) : isPopular ? (
                <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded bg-[var(--color-primary)]/15 text-[var(--color-primary)]">
                  {t('billingUi:planPicker.popular')}
                </span>
              ) : null}
            </div>
            <div className="mt-1 flex items-baseline gap-1">
              <span
                className="text-2xl font-bold tabular-nums"
                style={{ color: 'var(--color-text)' }}
              >
                {formatPriceBRL(plan.price)}
              </span>
              <span className="text-[12px] text-[var(--color-text-muted)]">{t('billingUi:common.perMonth')}</span>
            </div>
          </div>
          {isCurrent ? (
            <Check className="h-4 w-4 text-[var(--color-accent)] mt-2 shrink-0" />
          ) : (
            <ArrowRight className="h-4 w-4 text-[var(--color-text-muted)] group-hover:text-[var(--color-primary)] transition-colors mt-2 shrink-0" />
          )}
        </div>

        {highlights.length > 0 && (
          <ul className={cn('mt-4 grid grid-cols-1 gap-2', isMobile && 'grid-cols-2 gap-x-3')}>
            {highlights.map((h) => (
              <li key={h} className="flex items-start gap-2 text-[13px] text-[var(--color-text-muted)]">
                <Check className="h-4 w-4 mt-0.5 text-[var(--color-accent)] shrink-0" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}
      </button>
    );
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('billingUi:planPicker.title')}
      description={t('billingUi:planPicker.description')}
      size="md"
      forceMode={forceMode}
    >
      {candidates.length === 0 ? (
        <p className="py-8 text-center text-sm text-[var(--color-text-muted)]">
          {t('billingUi:planPicker.alreadyHighest')}
        </p>
      ) : isMobile ? (
        /* Drawer mobile: pilha vertical — sem carrossel, sem dots.
           Rolagem natural do sheet, um plano por linha, fácil de comparar. */
        <div className="space-y-3">
          <div className="flex flex-col gap-3">{candidates.map(renderCard)}</div>
          <p className="pt-1 pb-1 text-center text-[11px] text-[var(--color-text-muted)]">
            {t('billingUi:planPicker.paymentFooter')}
          </p>
        </div>
      ) : (
        <div className="relative">
          {/* Setas (desktop / qualquer tela com hover) — facilita
              navegação sem precisar arrastar/swipe. Escondidas nos
              extremos. */}
          {activeIdx > 0 && (
            <button
              type="button"
              onClick={() => scrollToIdx(activeIdx - 1)}
              aria-label={t('billingUi:planPicker.prevPlan')}
              className="hidden md:flex absolute left-1 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-[var(--color-surface)]/95 border border-[var(--color-border)] shadow-md hover:bg-[var(--color-surface-hover)] transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-[var(--color-text)]" />
            </button>
          )}
          {activeIdx < candidates.length - 1 && (
            <button
              type="button"
              onClick={() => scrollToIdx(activeIdx + 1)}
              aria-label={t('billingUi:planPicker.nextPlan')}
              className="hidden md:flex absolute right-1 top-1/2 -translate-y-1/2 z-10 h-9 w-9 items-center justify-center rounded-full bg-[var(--color-surface)]/95 border border-[var(--color-border)] shadow-md hover:bg-[var(--color-surface-hover)] transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-[var(--color-text)]" />
            </button>
          )}

          {/* Carousel — overflow-x scroll-snap. Cards w-[78%] deixam
              ~22% do próximo aparecendo de "peek" — afordância visual
              clara de "tem mais aqui, arraste". -mx-6/px-6 compensa
              padding do dialog. */}
          <div
            ref={scrollRef}
            className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar -mx-6 px-6 scroll-pl-6 scroll-pr-6 gap-3 pb-1"
            style={{ scrollbarWidth: 'none' }}
          >
            {candidates.map(renderCard)}
          </div>

          {/* Dots — clicáveis pra pular direto pro plano. Pill estende
              quando ativo (mais visível, padrão Apple). */}
          {candidates.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-4">
              {candidates.map((plan, i) => (
                <button
                  key={plan.slug}
                  type="button"
                  onClick={() => scrollToIdx(i)}
                  aria-label={t('billingUi:planPicker.goToPlan', { name: plan.name })}
                  className={cn(
                    'h-1.5 rounded-full transition-all',
                    i === activeIdx
                      ? 'w-6 bg-[var(--color-primary)]'
                      : 'w-1.5 bg-[var(--color-text-muted)]/40 hover:bg-[var(--color-text-muted)]/60',
                  )}
                />
              ))}
            </div>
          )}

          <p className="pt-3 text-center text-[11px] text-[var(--color-text-muted)]">
            {t('billingUi:planPicker.paymentFooter')}
          </p>
        </div>
      )}
    </ResponsiveDialog>
  );
}
